/**
 * PendingCheckInsTab Component
 * Shows pending check-ins grouped by urgency: Overdue, Due Today, Due This Week
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import { useUrlState } from '../../hooks/useUrlState';
import {
  AlertCircle,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Star,
  Users,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow, format, isPast, isToday } from 'date-fns';
import { Card, Badge, Button, LoadingSpinner, EmptyState, Avatar } from '../../components/common';
import { QuickCheckInModal } from '../../components/okr/QuickCheckInModal';
import { usePendingCheckIns, useTeamPendingCheckIns, useOrgPendingCheckIns } from '../../hooks';
import type { PendingCheckInItem, CheckInStatus } from '../../types/okr';
import { Building2 } from 'lucide-react';

interface PendingCheckInsTabProps {
  isManager: boolean;
  isAccountAdmin?: boolean;
}

const statusConfig: Record<CheckInStatus, { icon: typeof CheckCircle; color: string; bgColor: string }> = {
  on_track: { icon: CheckCircle, color: 'text-success-600', bgColor: 'bg-success-50' },
  at_risk: { icon: AlertTriangle, color: 'text-warning-600', bgColor: 'bg-warning-50' },
  off_track: { icon: AlertCircle, color: 'text-danger-600', bgColor: 'bg-danger-50' },
};

interface PendingCheckInCardProps {
  item: PendingCheckInItem;
  onQuickCheckIn: (item: PendingCheckInItem) => void;
  showOwner?: boolean;
}

const PendingCheckInCard: React.FC<PendingCheckInCardProps> = ({ item, onQuickCheckIn, showOwner = false }) => {
  const openWithReturn = useOpenWithReturn();
  const dueDate = new Date(item.nextCheckInDue);
  const isOverdue = isPast(dueDate) && !isToday(dueDate);
  const isDueToday = isToday(dueDate);

  const statusInfo = item.lastCheckInStatus ? statusConfig[item.lastCheckInStatus] : null;
  const StatusIcon = statusInfo?.icon;

  return (
    <Card className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* OKR Title */}
          <h4 className="font-medium text-stone-900 truncate">{item.okrTitle}</h4>

          {/* Owner (for team view) */}
          {showOwner && item.owner && (
            <div className="flex items-center gap-2 mt-1">
              <Avatar
                size="sm"
                src={item.owner.avatar || undefined}
                fallbackText={`${item.owner.firstName} ${item.owner.lastName}`}
              />
              <span className="text-xs text-stone-500">
                {item.owner.firstName} {item.owner.lastName}
              </span>
            </div>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-stone-500">
            {/* Due date */}
            <span className={`flex items-center gap-1 ${isOverdue ? 'text-danger-600 font-medium' : isDueToday ? 'text-warning-600 font-medium' : ''}`}>
              <Clock className="h-3.5 w-3.5" />
              {isOverdue ? `Overdue by ${formatDistanceToNow(dueDate)}` : isDueToday ? 'Due today' : `Due ${format(dueDate, 'MMM d')}`}
            </span>

            {/* Last status */}
            {statusInfo && StatusIcon && (
              <span className={`flex items-center gap-1 ${statusInfo.color}`}>
                <StatusIcon className="h-3.5 w-3.5" />
                {item.lastCheckInStatus?.replace('_', ' ')}
              </span>
            )}

            {/* Last confidence */}
            {item.lastConfidenceLevel && (
              <span className="flex items-center gap-1">
                <Star className="h-3.5 w-3.5 text-warning-500 fill-warning-500" />
                {item.lastConfidenceLevel}/5
              </span>
            )}

            {/* Progress */}
            <span className="flex items-center gap-1">
              {item.progress}% complete
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => onQuickCheckIn(item)}
          >
            Check in
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openWithReturn(`/okrs/${item.okrId}`)}
            className="text-stone-500"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

interface CheckInGroupProps {
  title: string;
  items: PendingCheckInItem[];
  icon: typeof AlertCircle;
  iconColor: string;
  onQuickCheckIn: (item: PendingCheckInItem) => void;
  showOwner?: boolean;
}

const CheckInGroup: React.FC<CheckInGroupProps> = ({
  title,
  items,
  icon: Icon,
  iconColor,
  onQuickCheckIn,
  showOwner
}) => {
  if (items.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-5 w-5 ${iconColor}`} />
        <h3 className="font-semibold text-stone-700">
          {title} ({items.length})
        </h3>
      </div>
      <div className="space-y-3">
        {items.map((item) => (
          <PendingCheckInCard
            key={item.okrId}
            item={item}
            onQuickCheckIn={onQuickCheckIn}
            showOwner={showOwner}
          />
        ))}
      </div>
    </div>
  );
};

export const PendingCheckInsTab: React.FC<PendingCheckInsTabProps> = ({ isManager, isAccountAdmin = false }) => {
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<PendingCheckInItem | null>(null);
  // In the URL, so opening an OKR and coming Back keeps the My/Team/Org choice.
  const [view, setView] = useUrlState({ pview: 'my' }, { pview: ['my', 'team', 'org'] });
  const viewMode = view.pview as 'my' | 'team' | 'org';
  const setViewMode = (mode: 'my' | 'team' | 'org') => setView({ pview: mode });

  // Fetch data
  const { data: myPending, isLoading: loadingMy, error: errorMy, refetch: refetchMy } = usePendingCheckIns();
  const { data: teamPending, isLoading: loadingTeam, refetch: refetchTeam } = useTeamPendingCheckIns();
  const { data: orgPending, isLoading: loadingOrg, refetch: refetchOrg } = useOrgPendingCheckIns();

  const handleQuickCheckIn = (item: PendingCheckInItem) => {
    setSelectedItem(item);
  };

  const handleCheckInSuccess = () => {
    setSelectedItem(null);
    refetchMy();
    if (isManager) {
      refetchTeam();
    }
    if (isAccountAdmin) {
      refetchOrg();
    }
  };

  const isLoading = loadingMy || (isManager && loadingTeam) || (isAccountAdmin && loadingOrg);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading check-ins..." />
      </div>
    );
  }

  if (errorMy) {
    return (
      <Card className="p-6">
        <div className="text-center text-danger-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          <p>Failed to load check-ins. Please try again.</p>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => refetchMy()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  const myTotal = (myPending?.overdue.length || 0) + (myPending?.dueToday.length || 0) + (myPending?.dueThisWeek.length || 0);
  const teamTotal = isManager
    ? (teamPending?.overdue.length || 0) + (teamPending?.dueToday.length || 0) + (teamPending?.dueThisWeek.length || 0)
    : 0;
  const orgTotal = isAccountAdmin
    ? (orgPending?.overdue.length || 0) + (orgPending?.dueToday.length || 0) + (orgPending?.dueThisWeek.length || 0)
    : 0;

  const hasNoPending = myTotal === 0 && teamTotal === 0 && orgTotal === 0;
  const showToggle = (isManager && teamTotal > 0) || (isAccountAdmin && orgTotal > 0);

  return (
    <div>
      {/* Toggle for managers and account admins */}
      {showToggle && (
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setViewMode('my')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'my'
                ? 'bg-primary-100 text-primary-700'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            My Check-ins
            {myTotal > 0 && (
              <Badge variant={myPending?.overdue.length ? 'danger' : 'secondary'} size="sm">
                {myTotal}
              </Badge>
            )}
          </button>
          {isManager && teamTotal > 0 && (
            <button
              onClick={() => setViewMode('team')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'team'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Users className="h-4 w-4" />
              Team Check-ins
              {teamTotal > 0 && (
                <Badge variant={teamPending?.overdue.length ? 'danger' : 'secondary'} size="sm">
                  {teamTotal}
                </Badge>
              )}
            </button>
          )}
          {isAccountAdmin && orgTotal > 0 && (
            <button
              onClick={() => setViewMode('org')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'org'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              <Building2 className="h-4 w-4" />
              Organization
              {orgTotal > 0 && (
                <Badge variant={orgPending?.overdue.length ? 'danger' : 'secondary'} size="sm">
                  {orgTotal}
                </Badge>
              )}
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {hasNoPending ? (
        <EmptyState
          icon={CheckCircle}
          title="All caught up!"
          description="You have no pending check-ins. Great job staying on top of your OKRs!"
          action={{
            label: 'View My OKRs',
            onClick: () => navigate('/okrs')
          }}
        />
      ) : (
        <>
          {/* My Check-ins */}
          {viewMode === 'my' && myPending && (
            <>
              <CheckInGroup
                title="Overdue"
                items={myPending.overdue}
                icon={AlertCircle}
                iconColor="text-danger-600"
                onQuickCheckIn={handleQuickCheckIn}
              />
              <CheckInGroup
                title="Due Today"
                items={myPending.dueToday}
                icon={Clock}
                iconColor="text-warning-600"
                onQuickCheckIn={handleQuickCheckIn}
              />
              <CheckInGroup
                title="Due This Week"
                items={myPending.dueThisWeek}
                icon={Calendar}
                iconColor="text-primary-600"
                onQuickCheckIn={handleQuickCheckIn}
              />

              {myTotal === 0 && (
                <EmptyState
                  icon={CheckCircle}
                  title="No pending check-ins"
                  description="You're all caught up with your personal check-ins!"
                />
              )}
            </>
          )}

          {/* Team Check-ins */}
          {viewMode === 'team' && teamPending && (
            <>
              <CheckInGroup
                title="Team Overdue"
                items={teamPending.overdue}
                icon={AlertCircle}
                iconColor="text-danger-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />
              <CheckInGroup
                title="Team Due Today"
                items={teamPending.dueToday}
                icon={Clock}
                iconColor="text-warning-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />
              <CheckInGroup
                title="Team Due This Week"
                items={teamPending.dueThisWeek}
                icon={Calendar}
                iconColor="text-primary-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />

              {teamTotal === 0 && (
                <EmptyState
                  icon={CheckCircle}
                  title="Team is all caught up"
                  description="Your team has no pending check-ins!"
                />
              )}
            </>
          )}

          {/* Organization Check-ins (Account Admin only) */}
          {viewMode === 'org' && orgPending && (
            <>
              <CheckInGroup
                title="Organization Overdue"
                items={orgPending.overdue}
                icon={AlertCircle}
                iconColor="text-danger-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />
              <CheckInGroup
                title="Organization Due Today"
                items={orgPending.dueToday}
                icon={Clock}
                iconColor="text-warning-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />
              <CheckInGroup
                title="Organization Due This Week"
                items={orgPending.dueThisWeek}
                icon={Calendar}
                iconColor="text-primary-600"
                onQuickCheckIn={handleQuickCheckIn}
                showOwner
              />

              {orgTotal === 0 && (
                <EmptyState
                  icon={CheckCircle}
                  title="Organization is all caught up"
                  description="No pending check-ins across the organization!"
                />
              )}
            </>
          )}
        </>
      )}

      {/* Quick Check-in Modal */}
      {selectedItem && (
        <QuickCheckInModal
          isOpen={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          okrId={selectedItem.okrId}
          okrTitle={selectedItem.okrTitle}
          onSuccess={handleCheckInSuccess}
        />
      )}
    </div>
  );
};
